import { db, type CompanyRow, type SiteRow, type UnitRow, type RefrigerationUnitRow } from '../db';
import { newId } from '../../utils/ids';
import { formatAddressParts } from '@/utils/address';
import { Company, Site } from '../schemas';
import type { z } from 'zod';
import { AbstractBaseRepo } from './baseRepo';
import { ActionsRepo } from './actionsRepo';
import { softDeleteSyncMetadata } from '../sync/syncMetadata';
import { queueForSync } from '../sync/syncOutbox';
import { ContactsRepo } from './contactsRepo';
import { trackEntityCreated, trackEntityUpdated, trackEntityDeleted } from '@/lib/metrics';

const ACTIVE_COMPANY_KEY = 'activeCompanyId';
const ACTIVE_SITE_KEY = 'activeSiteId';

class DuplicateCompanyFieldError extends Error {
  readonly field: 'code' | 'vat';
  constructor(field: 'code' | 'vat', message: string) {
    super(message);
    this.name = 'DuplicateCompanyFieldError';
    this.field = field;
  }
}

const optionalTrim = (value?: string | null): string | undefined =>
  typeof value === 'string' && value.trim().length ? value.trim() : undefined;

type CompanySchemaInput = z.input<typeof Company>;
export type CompanyInput = Partial<
  Omit<CompanySchemaInput, 'id' | 'name' | 'createdAt' | 'updatedAt' | 'personnel'>
> & {
  id?: CompanySchemaInput['id'];
  name: CompanySchemaInput['name'];
  createdAt?: CompanySchemaInput['createdAt'];
  updatedAt?: CompanySchemaInput['updatedAt'];
};

export type CompanyWithSites = Company & { sites: Site[] };

function toCompany(row: CompanyRow, personnel: Company['personnel']): Company {
  return Company.parse({
    ...row,
    personnel,
  });
}

function isActiveUnit(unit: UnitRow): boolean {
  // Use syncDeletedAt as the single source of truth for deletion status
  return !unit.syncDeletedAt;
}

function toSite(row: SiteRow, units: RefrigerationUnitRow[]): Site {
  // Units are already filtered by callers using syncDeletedAt
  return Site.parse({
    ...row,
    refrigerationUnits: units,
    refrigerationUnitsCount: units.length,
  });
}

function sortSitesBySequence(sites: Site[]): Site[] {
  return [...sites].sort((a, b) => {
    if (a.sequenceNumber !== b.sequenceNumber) {
      return a.sequenceNumber - b.sequenceNumber;
    }
    return a.name.localeCompare(b.name, 'it', { numeric: true });
  });
}

class CompaniesRepoImpl extends AbstractBaseRepo<Company, Company, CompanyRow, CompanyInput> {
  protected readonly table = db.companies;

  protected getRowId(row: CompanyRow): string {
    return row.id;
  }

  protected toListModel(row: CompanyRow): Company {
    return toCompany(row, []);
  }

  protected toGetModel(row: CompanyRow): Company {
    return toCompany(row, []);
  }

  protected getInputId(input: CompanyInput): string | null {
    return input.id ?? null;
  }

  protected async toRow(
    input: CompanyInput,
    ctx: { now: string; existing?: CompanyRow }
  ): Promise<CompanyRow> {
    const id = input.id ?? newId();
    const hasLogoUrl = Object.prototype.hasOwnProperty.call(input, 'logoUrl');
    const logoUrl = hasLogoUrl ? input.logoUrl : ctx.existing?.logoUrl;
    const trimmedCode = input.code?.trim() || undefined;
    const trimmedVat = input.vat?.trim() || undefined;

    const checkConflict = async (field: 'code' | 'vat', value?: string) => {
      if (!value) return;
      const existingCompany = await this.table
        .where(field)
        .equals(value)
        .filter((row) => row.id !== id)
        .first();
      if (!existingCompany) return;
      if (existingCompany.syncDeletedAt) {
        throw new DuplicateCompanyFieldError(
          field,
          field === 'code'
            ? 'Esiste già un’azienda archiviata con questo codice. Contatta un admin per ripristinarla o eliminarla definitivamente.'
            : 'Esiste già un’azienda archiviata con questa P.IVA. Contatta un admin per ripristinarla o eliminarla definitivamente.'
        );
      }
      throw new DuplicateCompanyFieldError(
        field,
        field === 'code'
          ? 'Esiste già un’azienda con questo codice.'
          : 'Esiste già un’azienda con questa P.IVA.'
      );
    };

    await Promise.all([checkConflict('code', trimmedCode), checkConflict('vat', trimmedVat)]);

    const sedeLegaleAddress =
      'sedeLegaleAddress' in input
        ? optionalTrim(input.sedeLegaleAddress)
        : ctx.existing?.sedeLegaleAddress;
    const sedeLegaleCap =
      'sedeLegaleCap' in input ? optionalTrim(input.sedeLegaleCap) : ctx.existing?.sedeLegaleCap;
    const sedeLegaleCity =
      'sedeLegaleCity' in input ? optionalTrim(input.sedeLegaleCity) : ctx.existing?.sedeLegaleCity;
    const sedeLegaleProvince =
      'sedeLegaleProvince' in input
        ? optionalTrim(input.sedeLegaleProvince)
        : ctx.existing?.sedeLegaleProvince;
    const composedSedeLegale = formatAddressParts({
      address: sedeLegaleAddress,
      cap: sedeLegaleCap,
      city: sedeLegaleCity,
      province: sedeLegaleProvince,
    });
    const nextSedeLegale = optionalTrim(input.sedeLegale) || composedSedeLegale;

    return {
      id,
      name: input.name.trim(),
      code: trimmedCode,
      vat: trimmedVat,
      sedeLegale: nextSedeLegale || undefined,
      sedeLegaleAddress,
      sedeLegaleCap,
      sedeLegaleCity,
      sedeLegaleProvince,
      notes: input.notes?.trim() || undefined,
      logoUrl,
      createdAt: input.id ? input.createdAt || ctx.existing?.createdAt || ctx.now : ctx.now,
      updatedAt: ctx.now,
      // Sync metadata - will be overwritten by baseRepo.save()
      syncUpdatedAt: ctx.existing?.syncUpdatedAt ?? ctx.now,
      syncDeletedAt: ctx.existing?.syncDeletedAt,
      syncDeviceId: ctx.existing?.syncDeviceId,
      syncVersion: ctx.existing?.syncVersion ?? 0,
    };
  }

  async getActiveContext(): Promise<{
    companyId: string | null;
    siteId: string | null;
  }> {
    const [companyRow, siteRow] = await Promise.all([
      db.kv.get(ACTIVE_COMPANY_KEY),
      db.kv.get(ACTIVE_SITE_KEY),
    ]);
    return {
      companyId: (companyRow?.value as string) || null,
      siteId: (siteRow?.value as string) || null,
    };
  }

  async setActiveContext({
    companyId,
    siteId,
  }: {
    companyId: string | null;
    siteId: string | null;
  }): Promise<void> {
    if (companyId) {
      await db.kv.put({ key: ACTIVE_COMPANY_KEY, value: companyId });
    } else {
      await db.kv.delete(ACTIVE_COMPANY_KEY);
    }

    if (siteId) {
      await db.kv.put({ key: ACTIVE_SITE_KEY, value: siteId });
    } else {
      await db.kv.delete(ACTIVE_SITE_KEY);
    }
  }

  async listCompanies(): Promise<Company[]> {
    return this.list();
  }

  override async list(): Promise<Company[]> {
    const rows = await this.table.orderBy('name').toArray();
    // Filter out soft-deleted records
    const activeRows = rows.filter((row) => !this.isDeleted(row));
    const personnelMap = await ContactsRepo.listByCompanies(activeRows.map((row) => row.id));
    return activeRows.map((row) => toCompany(row, personnelMap.get(row.id) ?? []));
  }

  async listCompaniesWithSites(): Promise<CompanyWithSites[]> {
    const [companies, sites, units] = await Promise.all([
      db.companies.orderBy('name').toArray(),
      db.sites.toArray(),
      db.units.toArray(),
    ]);

    // Filter out soft-deleted records
    const activeCompanies = companies.filter((c) => !c.syncDeletedAt);
    const activeSites = sites.filter((s) => !s.syncDeletedAt);
    const activeUnits = units.filter((unit) => !unit.syncDeletedAt);

    const sitesByCompany = new Map<string, SiteRow[]>();
    activeSites.forEach((site) => {
      const list = sitesByCompany.get(site.companyId);
      if (list) {
        list.push(site);
      } else {
        sitesByCompany.set(site.companyId, [site]);
      }
    });

    const unitsBySite = new Map<string, RefrigerationUnitRow[]>();
    activeUnits.forEach((unit) => {
      if (!isActiveUnit(unit)) return;
      const list = unitsBySite.get(unit.siteId);
      const mapped = {
        ...unit,
      } satisfies RefrigerationUnitRow;
      if (list) {
        list.push(mapped);
      } else {
        unitsBySite.set(unit.siteId, [mapped]);
      }
    });

    const personnelMap = await ContactsRepo.listByCompanies(
      activeCompanies.map((company) => company.id)
    );

    return activeCompanies.map((company) => ({
      ...toCompany(company, personnelMap.get(company.id) ?? []),
      sites: sortSitesBySequence(
        (sitesByCompany.get(company.id) || []).map((site) =>
          toSite(site, unitsBySite.get(site.id) ?? [])
        )
      ),
    }));
  }

  async getCompany(id: string): Promise<CompanyWithSites | null> {
    const companyRow = await this.table.get(id);
    if (!companyRow) return null;
    if (this.isDeleted(companyRow)) return null;
    const [sites, personnel, units] = await Promise.all([
      db.sites.where('companyId').equals(companyRow.id).toArray(),
      ContactsRepo.listByCompany(companyRow.id),
      db.units.where('companyId').equals(companyRow.id).toArray(),
    ]);
    // Filter out soft-deleted sites
    const activeSites = sites.filter((s) => !s.syncDeletedAt);
    const unitsBySite = new Map<string, RefrigerationUnitRow[]>();
    // Use syncDeletedAt as the single source of truth
    units
      .filter((unit) => !unit.syncDeletedAt)
      .forEach((unit) => {
        const list = unitsBySite.get(unit.siteId);
        const mapped = {
          ...unit,
        } satisfies RefrigerationUnitRow;
        if (list) {
          list.push(mapped);
        } else {
          unitsBySite.set(unit.siteId, [mapped]);
        }
      });
    return {
      ...toCompany(companyRow, personnel),
      sites: sortSitesBySequence(
        activeSites.map((site) => toSite(site, unitsBySite.get(site.id) ?? []))
      ),
    };
  }

  async saveCompany(input: CompanyInput): Promise<string> {
    const isUpdate = input.id ? await this.table.get(input.id) : null;
    const id = await this.save(input);
    if (isUpdate) {
      trackEntityUpdated('company');
    } else {
      trackEntityCreated('company');
    }
    return id;
  }

  async deleteCompany(id: string): Promise<void> {
    await db.transaction(
      'rw',
      [db.companies, db.sites, db.units, db.contacts, db.reports, db.actions, db.kv],
      async () => {
        trackEntityDeleted('company');
        const sites = await db.sites.where('companyId').equals(id).toArray();
        const units = await db.units.where('companyId').equals(id).toArray();
        const contacts = await db.contacts.where('companyId').equals(id).toArray();
        await Promise.all(
          sites.map(async (site) => {
            const syncMeta = softDeleteSyncMetadata(site);
            await db.sites.update(site.id, syncMeta);
            await queueForSync(db.sites.name, String(site.id), 'delete', {
              ...site,
              ...syncMeta,
            });
          })
        );

        await Promise.all(
          units.map(async (unit) => {
            const syncMeta = softDeleteSyncMetadata(unit);
            const deletedAt = syncMeta.syncDeletedAt;
            const nextRow = { ...unit, ...syncMeta, deletedAt };
            await db.units.update(unit.id, {
              ...syncMeta,
              deletedAt,
              updatedAt: syncMeta.syncUpdatedAt,
            });
            await queueForSync(db.units.name, String(unit.id), 'delete', nextRow);
          })
        );

        await Promise.all(contacts.map((contact) => ContactsRepo.remove(id, contact.id)));

        const actions = await db.actions.where('companyId').equals(id).toArray();
        await Promise.all(actions.map((action) => ActionsRepo.softDelete(action.id)));
        await this.softDelete(id);

        const active = await this.getActiveContext();
        if (active.companyId === id) {
          await this.setActiveContext({
            companyId: null,
            siteId: null,
          });
        }
      }
    );
  }
}

export const CompaniesRepo = new CompaniesRepoImpl();
export { DuplicateCompanyFieldError };
